import { generateApolloClient } from "@/common/apolloClientUtil";
import { getCityByPrefGroupedByStepA } from "@/common/graphql/cityByPref";
import { getSectionByPrefCityGroupedByStepA } from "@/common/graphql/sectionByPrefCity";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import {
  GetBlockByPrefCitySectionDocument,
  type GetBlockByPrefCitySectionQuery,
  GetBlockByZipCodeDocument,
  type GetBlockByZipCodeQuery,
  GetZipCodeByPrefCitySectionBlockDocument,
  type GetZipCodeByPrefCitySectionBlockQuery,
  GetZipCodeByPrefCitySectionDocument,
  type GetZipCodeByPrefCitySectionQuery,
  ListDeliveryAreaSectionUnassignedDocument,
  type ListDeliveryAreaSectionUnassignedQuery,
} from "@/generated/graphql";
import type { ApolloQueryResult } from "@apollo/client/core";
import type {
  AddressBlockItem,
  AddressResolvedPayload,
  AddressSearchByZipResult,
  AddressSelectorProvider,
  AddressSelectorQueryInput,
  KanaStepA,
} from "../types";

/** 会員住所選択の反映先を表す型。 */
type MemberAddressWritable = {
  zip_code: string;
  address1: string;
};

/** 会員住所選択プロバイダに必要な依存をまとめた型。 */
export interface MemberAddressProviderContext {
  createMember: MemberAddressWritable;
  wordList: Record<string, string>;
}

/** カナ行一覧の空データを生成する。 */
const createEmptyKanaStepA = (): KanaStepA => ({
  line_a: [],
  line_ka: [],
  line_sa: [],
  line_ta: [],
  line_na: [],
  line_ha: [],
  line_ma: [],
  line_ya: [],
  line_ra: [],
  line_wa: [],
  line_other: [],
});

/** 先頭カナから五十音行キーを判定する。 */
const resolveKanaLineKey = (
  kanaHead: string
): keyof Pick<
  KanaStepA,
  | "line_a"
  | "line_ka"
  | "line_sa"
  | "line_ta"
  | "line_na"
  | "line_ha"
  | "line_ma"
  | "line_ya"
  | "line_ra"
  | "line_wa"
  | "line_other"
> => {
  if (["ｱ", "ｲ", "ｳ", "ｴ", "ｵ"].includes(kanaHead)) {
    return "line_a";
  }
  if (["ｶ", "ｷ", "ｸ", "ｹ", "ｺ"].includes(kanaHead)) {
    return "line_ka";
  }
  if (["ｻ", "ｼ", "ｽ", "ｾ", "ｿ"].includes(kanaHead)) {
    return "line_sa";
  }
  if (["ﾀ", "ﾁ", "ﾂ", "ﾃ", "ﾄ"].includes(kanaHead)) {
    return "line_ta";
  }
  if (["ﾅ", "ﾆ", "ﾇ", "ﾈ", "ﾉ"].includes(kanaHead)) {
    return "line_na";
  }
  if (["ﾊ", "ﾋ", "ﾌ", "ﾍ", "ﾎ"].includes(kanaHead)) {
    return "line_ha";
  }
  if (["ﾏ", "ﾐ", "ﾑ", "ﾒ", "ﾓ"].includes(kanaHead)) {
    return "line_ma";
  }
  if (["ﾔ", "ﾕ", "ﾖ"].includes(kanaHead)) {
    return "line_ya";
  }
  if (["ﾗ", "ﾘ", "ﾙ", "ﾚ", "ﾛ"].includes(kanaHead)) {
    return "line_ra";
  }
  if (kanaHead === "ﾜ") {
    return "line_wa";
  }
  return "line_other";
};

/** 空丁目を「その他」表示名へ変換する。 */
const toOtherLabel = (value: string | null | undefined, otherLabel: string): string => {
  if (!value || value.trim() === "") {
    return otherLabel;
  }
  return value;
};

/** 解決済み住所データから住所文字列を生成する。 */
const buildAddress = (payload: AddressResolvedPayload): string => {
  return (
    (payload.pref_name ?? "") +
    (payload.city_name ?? "") +
    (payload.section_name ?? "") +
    (payload.block_name ?? "")
  );
};

/** 郵便番号検索結果を市区/町域/丁目に分類する。 */
const classifyZipSearch = (
  rows: NonNullable<GetBlockByZipCodeQuery["getBlockByZipCode"]>,
  otherLabel: string
): Pick<AddressSearchByZipResult, "cityBlockList" | "sectionBlockList" | "blockList"> => {
  if (rows.length === 0) {
    return {
      cityBlockList: null,
      sectionBlockList: null,
      blockList: [],
    };
  }

  let prevCity = "";
  let prevSection = "";
  let cityFixed = true;
  let sectionFixed = true;

  for (const row of rows) {
    if (!row?.section_name) {
      continue;
    }

    if (!prevCity) {
      prevCity = row.city_name;
    } else if (prevCity !== row.city_name) {
      cityFixed = false;
    }

    if (!prevSection) {
      prevSection = row.section_name;
    } else if (prevSection !== row.section_name) {
      sectionFixed = false;
    }
  }

  if (cityFixed && sectionFixed) {
    return {
      cityBlockList: null,
      sectionBlockList: null,
      blockList: rows
        .filter((row): row is NonNullable<typeof row> => row !== null && row !== undefined)
        .map((row) => ({
          name: toOtherLabel(row.block_name, otherLabel),
          enabled: true,
        })),
    };
  }

  if (cityFixed) {
    const sectionBlockList = createEmptyKanaStepA();
    const sectionNames = new Set<string>();
    for (const row of rows) {
      if (!row?.section_name) {
        continue;
      }
      const sectionName = row.section_name.replace("　", "");
      if (sectionNames.has(sectionName)) {
        continue;
      }
      sectionNames.add(sectionName);
      const kanaHead = row.section_kana?.substring(0, 1) ?? "";
      const lineKey = resolveKanaLineKey(kanaHead);
      sectionBlockList[lineKey]?.push(sectionName);
    }
    return {
      cityBlockList: null,
      sectionBlockList,
      blockList: [],
    };
  }

  const cityBlockList = createEmptyKanaStepA();
  const cityNames = new Set<string>();
  for (const row of rows) {
    if (!row?.city_name) {
      continue;
    }
    const cityName = row.city_name.replace("　", "");
    if (cityNames.has(cityName)) {
      continue;
    }
    cityNames.add(cityName);
    const kanaHead = row.city_kana?.substring(0, 1) ?? "";
    const lineKey = resolveKanaLineKey(kanaHead);
    cityBlockList[lineKey]?.push(cityName);
  }

  return {
    cityBlockList,
    sectionBlockList: null,
    blockList: [],
  };
};

/** 会員向け住所選択プロバイダを作成する。 */
export const createMemberAddressProvider = (): AddressSelectorProvider<MemberAddressProviderContext> => {
  return {
    /** 郵便番号の入力値を検証する。 */
    validateZip(zipCode: string): string | null {
      if (zipCode.length < 7) {
        return "郵便番号が入力されていません。";
      }
      return null;
    },

    /** 郵便番号から住所候補を取得する。 */
    async searchByZip(input, context): Promise<AddressSearchByZipResult> {
      const apolloClient = await generateApolloClient();
      const zipCode = input.zipCode ?? "";
      const otherLabel = context?.wordList?.lbl_other_block_disp_name ?? "その他";

      const result: ApolloQueryResult<GetBlockByZipCodeQuery> =
        await apolloClient.query<GetBlockByZipCodeQuery>({
          query: GetBlockByZipCodeDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: localStorageGetChainCode(),
            shop_code: localStorageGetShopCode(),
            zip_code: zipCode,
          },
          fetchPolicy: "no-cache",
        });

      const rows = result.data.getBlockByZipCode ?? [];
      if (rows.length === 0) {
        return {
          cityBlockList: null,
          sectionBlockList: null,
          blockList: [],
        };
      }

      if (rows.length === 1 && rows[0]) {
        const row = rows[0];
        return {
          cityBlockList: null,
          sectionBlockList: null,
          blockList: [],
          selectedPref: row.pref_name,
          selectedCity: row.city_name.replace("　", ""),
          selectedSection: row.section_name.replace("　", ""),
          resolved: {
            zip_code: zipCode,
            address1:
              row.pref_name +
              row.city_name.replace("　", "") +
              row.section_name.replace("　", "") +
              (row.block_name ?? ""),
            pref_name: row.pref_name,
            city_name: row.city_name.replace("　", ""),
            section_name: row.section_name.replace("　", ""),
            block_name: row.block_name ?? "",
          },
        };
      }

      const base = classifyZipSearch(rows, otherLabel);

      // 既存実装との差分: provider 層で配達対象外区画を blockList に合流する。
      if (base.blockList.length > 0) {
        const first = rows[0];
        const selectedAddress =
          (first?.pref_name ?? "") +
          (first?.city_name?.replace("　", "") ?? "") +
          (first?.section_name?.replace("　", "") ?? "");

        const unassignedResult: ApolloQueryResult<ListDeliveryAreaSectionUnassignedQuery> =
          await apolloClient.query<ListDeliveryAreaSectionUnassignedQuery>({
            query: ListDeliveryAreaSectionUnassignedDocument,
            variables: {
              input: {
                customer_code: localStorageGetCustomerCode(),
                chain_code: localStorageGetChainCode(),
                shop_code: localStorageGetShopCode(),
                zip_code: zipCode,
              },
            },
            fetchPolicy: "no-cache",
          });

        const unassignedBlocks: AddressBlockItem[] =
          unassignedResult.data.listDeliveryAreaSectionUnassigned?.map((block) => ({
            name:
              block?.delivery_area_section?.replace(selectedAddress, "") || otherLabel,
            enabled: false,
          })) ?? [];

        return {
          ...base,
          selectedPref: first?.pref_name ?? "",
          selectedCity: first?.city_name?.replace("　", "") ?? "",
          selectedSection: first?.section_name?.replace("　", "") ?? "",
          blockList: base.blockList.concat(unassignedBlocks),
        };
      }

      return {
        ...base,
        selectedPref: rows[0]?.pref_name ?? "",
        selectedCity:
          base.blockList.length > 0 || base.sectionBlockList !== null
            ? rows[0]?.city_name?.replace("　", "") ?? ""
            : "",
        selectedSection:
          base.blockList.length > 0
            ? rows[0]?.section_name?.replace("　", "") ?? ""
            : "",
      };
    },

    /** 都道府県から市区候補を取得する。 */
    async getCityListByPref(input): Promise<KanaStepA | null> {
      const pref = input.pref ?? "";
      return (
        (await getCityByPrefGroupedByStepA(
        localStorageGetCustomerCode(),
        localStorageGetChainCode(),
        localStorageGetShopCode(),
        pref
      )) ?? null
      );
    },

    /** 都道府県と市区から町域候補を取得する。 */
    async getSectionListByPrefCity(input): Promise<KanaStepA | null> {
      const pref = input.pref ?? "";
      const city = input.city ?? "";
      return (
        (await getSectionByPrefCityGroupedByStepA(
        localStorageGetCustomerCode(),
        localStorageGetChainCode(),
        localStorageGetShopCode(),
        pref,
        city
      )) ?? null
      );
    },

    /** 都道府県・市区・町域から丁目候補を取得する。 */
    async getBlockListByPrefCitySection(input, context): Promise<AddressBlockItem[]> {
      const apolloClient = await generateApolloClient();
      const otherLabel = context?.wordList?.lbl_other_block_disp_name ?? "その他";

      const section = input.section === "その他" ? "" : input.section ?? "";

      const result: ApolloQueryResult<GetBlockByPrefCitySectionQuery> =
        await apolloClient.query<GetBlockByPrefCitySectionQuery>({
          query: GetBlockByPrefCitySectionDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: localStorageGetChainCode(),
            shop_code: localStorageGetShopCode(),
            pref: input.pref ?? "",
            city: input.city ?? "",
            section,
          },
          fetchPolicy: "no-cache",
        });

      const blocks = (result.data.getBlockByPrefCitySection ?? []).map((block) => ({
        name: toOtherLabel(block, otherLabel),
        enabled: true,
      }));

      // 既存実装準拠: 都道府県/市区/町域経由でも配達対象外区画を表示対象へ追加する。
      const zipResult: ApolloQueryResult<GetZipCodeByPrefCitySectionQuery> =
        await apolloClient.query<GetZipCodeByPrefCitySectionQuery>({
          query: GetZipCodeByPrefCitySectionDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: localStorageGetChainCode(),
            shop_code: localStorageGetShopCode(),
            pref: input.pref ?? "",
            city: input.city ?? "",
            section,
          },
          fetchPolicy: "no-cache",
        });

      const zipCode = zipResult.data.getZipCodeByPrefCitySection;
      if (!zipCode) {
        return blocks;
      }

      const selectedAddress = (input.pref ?? "") + (input.city ?? "") + section;
      const unassignedResult: ApolloQueryResult<ListDeliveryAreaSectionUnassignedQuery> =
        await apolloClient.query<ListDeliveryAreaSectionUnassignedQuery>({
          query: ListDeliveryAreaSectionUnassignedDocument,
          variables: {
            input: {
              customer_code: localStorageGetCustomerCode(),
              chain_code: localStorageGetChainCode(),
              shop_code: localStorageGetShopCode(),
              zip_code: zipCode,
            },
          },
          fetchPolicy: "no-cache",
        });

      const unassignedBlocks: AddressBlockItem[] =
        unassignedResult.data.listDeliveryAreaSectionUnassigned?.map((block) => ({
          name:
            block?.delivery_area_section?.replace(selectedAddress, "") || otherLabel,
          enabled: false,
        })) ?? [];

      return blocks.concat(unassignedBlocks);
    },

    /** 都道府県・市区・町域から郵便番号を解決する。 */
    async resolveZipByPrefCitySection(input): Promise<AddressResolvedPayload | null> {
      const apolloClient = await generateApolloClient();
      const section = input.section === "その他" ? "" : input.section ?? "";

      const result: ApolloQueryResult<GetZipCodeByPrefCitySectionQuery> =
        await apolloClient.query<GetZipCodeByPrefCitySectionQuery>({
          query: GetZipCodeByPrefCitySectionDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: localStorageGetChainCode(),
            shop_code: localStorageGetShopCode(),
            pref: input.pref ?? "",
            city: input.city ?? "",
            section,
          },
          fetchPolicy: "no-cache",
        });

      const zipCode = result.data.getZipCodeByPrefCitySection;
      if (!zipCode) {
        return null;
      }

      return {
        zip_code: zipCode,
        address1: (input.pref ?? "") + (input.city ?? "") + section,
        pref_name: input.pref,
        city_name: input.city,
        section_name: section,
        block_name: "",
      };
    },

    /** 都道府県・市区・町域・丁目から郵便番号を解決する。 */
    async resolveZipByPrefCitySectionBlock(input): Promise<AddressResolvedPayload | null> {
      const apolloClient = await generateApolloClient();
      const block = input.block === "その他" ? "" : input.block ?? "";

      const result: ApolloQueryResult<GetZipCodeByPrefCitySectionBlockQuery> =
        await apolloClient.query<GetZipCodeByPrefCitySectionBlockQuery>({
          query: GetZipCodeByPrefCitySectionBlockDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: localStorageGetChainCode(),
            shop_code: localStorageGetShopCode(),
            pref: input.pref ?? "",
            city: input.city ?? "",
            section: input.section ?? "",
            block,
          },
          fetchPolicy: "no-cache",
        });

      const zipCode = result.data.getZipCodeByPrefCitySectionBlock;
      if (!zipCode) {
        return null;
      }

      return {
        zip_code: zipCode,
        address1: (input.pref ?? "") + (input.city ?? "") + (input.section ?? "") + block,
        pref_name: input.pref,
        city_name: input.city,
        section_name: input.section,
        block_name: block,
      };
    },

    /** 解決済み住所を会員入力モデルへ反映する。 */
    applyResolvedAddress(payload, context): void {
      if (!context) {
        return;
      }

      context.createMember.zip_code = payload.zip_code;
      context.createMember.address1 = payload.address1 || buildAddress(payload);
    },
  };
};
